/**
 * Hand Pose Library sidebar panel — browse, save, apply, and manage hand poses.
 *
 * Shown in the pose-mode sidebar when any hand or finger bone is selected.
 * Lists built-in hand pose presets followed by user-created cloud library
 * hand poses, with the same interaction patterns as PoseLibraryPanel:
 *
 * - Tapping a row opens a confirm-apply popover.
 * - A three-dots menu provides Rename, Overwrite, Duplicate, and Delete.
 * - "Save Current Hand Pose" captures the current finger bone deltas.
 */

import { Check, Plus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  applyHandPose,
  DEFAULT_HAND_POSES,
  type HandPose,
  selectedHandSides,
} from "../app/defaultHandPoses.ts";
import {
  loadHandPoseFromLibrary,
  overwriteHandPoseInLibrary,
  saveHandPoseToLibrary,
} from "../app/handPoseLibrary.ts";
import { useLibraryStore } from "../app/libraryStore.ts";
import { duplicateLibraryItem } from "../app/poseLibrary.ts";
import { useStore } from "../app/store.ts";
import { Button, IconButton } from "./Button.tsx";
import { showConfirmDialog } from "./ConfirmDialog.tsx";
import { LibraryItemRow } from "./LibraryItemRow.tsx";
import { Popover } from "./Popover.tsx";
import { SearchField } from "./shared.tsx";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sideLabel(sides: Set<"L" | "R">): string {
  if (sides.size === 2) return "Both Hands";
  return sides.has("L") ? "Left Hand" : "Right Hand";
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function HandPoseLibraryPanel({ objectId }: { objectId: string }) {
  const selectedBones = useStore((s) => s.poseMode?.selectedBones);
  const items = useLibraryStore((s) => s.items);
  const fetchAll = useLibraryStore((s) => s.fetchAll);
  const deleteItems = useLibraryStore((s) => s.deleteItems);
  const renameItem = useLibraryStore((s) => s.renameItem);

  const sides = useMemo(
    () => selectedHandSides(selectedBones ?? new Set()),
    [selectedBones],
  );

  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [search, setSearch] = useState("");
  const saveInputRef = useRef<HTMLInputElement>(null);

  // Confirm-apply popover state for default presets
  const [confirmApplyDefault, setConfirmApplyDefault] = useState<string | null>(
    null,
  );
  const confirmApplyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Don't render if no hand bones are selected
  if (sides.size === 0) return null;

  // Filter default presets by search
  const filteredDefaults = search
    ? DEFAULT_HAND_POSES.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()),
      )
    : DEFAULT_HAND_POSES;

  // Filter saved hand poses by search
  const handPoseItems = (() => {
    let filtered = items.filter((i) => i.type === "hand_pose");
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => i.name.toLowerCase().includes(q));
    }
    return filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  })();

  const totalCount = filteredDefaults.length + handPoseItems.length;

  const handleSave = async () => {
    const name =
      newName.trim() || `Hand Pose ${new Date().toLocaleDateString()}`;
    setSaving(true);
    await saveHandPoseToLibrary(objectId, name);
    setNewName("");
    setShowSaveInput(false);
    setSaving(false);
    fetchAll();
  };

  const handleApplyDefault = (pose: HandPose) => {
    applyHandPose(pose, objectId, sides);
  };

  const handleApply = async (fileUrl: string) => {
    await loadHandPoseFromLibrary(fileUrl, objectId, sides);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirmDialog(
      "Delete this hand pose? This cannot be undone.",
    );
    if (confirmed) await deleteItems([id]);
  };

  const inputStyle = PANEL_INPUT_STYLE;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflow: "hidden",
      }}
    >
      {/* Save current hand pose */}
      {!showSaveInput ? (
        <Button
          onClick={() => setShowSaveInput(true)}
          variant="ghost"
          fullWidth
          leftIcon={<Plus size={14} />}
          style={{
            padding: "8px 10px",
            borderRadius: RADIUS_SM,
            border: `0.5px dashed ${COLOR_BORDER_STRONG}`,
            color: COLOR_TEXT_DIM,
            fontSize: FONT_SIZE_SMALL,
            marginBottom: 6,
            justifyContent: "flex-start",
          }}
        >
          Save Current Hand Pose
        </Button>
      ) : (
        <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
          <input
            ref={saveInputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setShowSaveInput(false);
            }}
            placeholder="Hand pose name"
            style={inputStyle}
          />
          <IconButton
            onClick={handleSave}
            disabled={saving}
            icon={<Check size={14} />}
            label="Save hand pose"
            style={{ color: COLOR_ACCENT }}
          />
          <IconButton
            onClick={() => setShowSaveInput(false)}
            icon={<X size={14} />}
            label="Cancel"
            style={{ color: COLOR_TEXT_DIM }}
          />
        </div>
      )}

      {/* Search */}
      {totalCount > 3 && (
        <div style={{ marginBottom: 6 }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search hand poses…"
          />
        </div>
      )}

      {/* Pose list */}
      <div
        style={{
          maxHeight: 300,
          overflowY: "auto",
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Default (built-in) presets */}
        {filteredDefaults.length > 0 && (
          <>
            <div
              style={{
                padding: "4px 4px 2px",
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_FAINT,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Presets
            </div>
            {filteredDefaults.map((pose) => {
              const isConfirming = confirmApplyDefault === pose.id;
              return (
                <div
                  key={pose.id}
                  ref={isConfirming ? confirmApplyRef : undefined}
                  onClick={() => {
                    setConfirmApplyDefault(pose.id);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 4px",
                    borderRadius: RADIUS_SM,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: FONT_SIZE_SMALL,
                        color: COLOR_TEXT_SECONDARY,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pose.name}
                    </div>
                  </div>
                  {isConfirming && (
                    <Popover
                      open
                      anchorRef={
                        confirmApplyRef as React.RefObject<HTMLElement>
                      }
                      onClose={() => setConfirmApplyDefault(null)}
                      style={{ padding: 8 }}
                    >
                      <Button
                        data-popover-item
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyDefault(pose);
                          setConfirmApplyDefault(null);
                        }}
                        variant="ghost"
                        size="sm"
                        leftIcon={<Check size={14} />}
                        style={{
                          padding: "6px 14px",
                          color: COLOR_ACCENT,
                          fontSize: FONT_SIZE_BODY,
                        }}
                      >
                        Apply to {sideLabel(sides)}
                      </Button>
                    </Popover>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* User-created cloud hand poses */}
        {handPoseItems.length > 0 && (
          <div
            style={{
              padding: "6px 4px 2px",
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Saved
          </div>
        )}

        {handPoseItems.length === 0 && filteredDefaults.length === 0 && (
          <div
            style={{
              padding: "12px 4px",
              textAlign: "center",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_SMALL,
            }}
          >
            {search ? "No matching hand poses" : "No saved hand poses yet"}
          </div>
        )}

        {handPoseItems.map((item) => (
          <LibraryItemRow
            key={item.id}
            id={item.id}
            name={item.name}
            subtitle={new Date(item.createdAt).toLocaleDateString()}
            thumbnailUrl={item.thumbnailUrl}
            applyLabel={`Apply to ${sideLabel(sides)}`}
            onApply={() => item.fileUrl && handleApply(item.fileUrl)}
            onRename={(newName) => renameItem(item.id, newName)}
            onOverwrite={() => overwriteHandPoseInLibrary(item.id, objectId)}
            onDuplicate={() => duplicateLibraryItem(item.id)}
            onDelete={() => handleDelete(item.id)}
          />
        ))}
      </div>
    </div>
  );
}
